import React from "react";
import { Loader2, Check } from "lucide-react";

const PasswordPolicyHint = () => (
  <div className="text-xs text-[#94a3b8] mt-1 space-y-0.5">
    <p>Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char</p>
  </div>
);

/**
 * AddStaffForm — new-member form used inside TeamHubTab.
 *
 * Extracted from TeamHubTab.js (iter150 refactor). Behaviour preserved,
 * all data-testids kept for E2E parity.
 */
export const AddStaffForm = ({
  form,
  setForm,
  ROLES,
  DEPARTMENTS,
  availableDesignations,
  managerCandidates,
  candidatesRole,
  headcountHint,
  addError,
  addLoading,
  addManager,
  onCancel,
}) => (
  <div className="p-5 rounded-2xl bg-[#FFFAF5] border border-[#f0e6d9] mb-4 space-y-3" data-testid="add-staff-form">
    <p className="font-semibold text-sm text-[#1E1B4B] mb-2">New Staff Member</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Full Name *"
        className="px-3 py-2.5 rounded-xl border text-sm focus:border-[#E83E8C] outline-none"
        data-testid="staff-name-input" />
      <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
        placeholder="Username *"
        className="px-3 py-2.5 rounded-xl border text-sm focus:border-[#E83E8C] outline-none"
        data-testid="staff-username-input" />
      <div>
        <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder="Password *" type="password"
          className="px-3 py-2.5 rounded-xl border text-sm focus:border-[#E83E8C] outline-none w-full"
          data-testid="staff-password-input" />
        <PasswordPolicyHint />
      </div>
      <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
        className="px-3 py-2.5 rounded-xl border text-sm focus:border-[#E83E8C] outline-none"
        data-testid="staff-role-select">
        {availableDesignations.length === 0 ? (
          Object.entries(ROLES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))
        ) : (
          <>
            <optgroup label="Built-in Roles">
              {availableDesignations.filter((d) => d.builtin).map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </optgroup>
            {availableDesignations.some((d) => !d.builtin) && (
              <optgroup label="Custom Designations">
                {availableDesignations.filter((d) => !d.builtin).map((d) => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </optgroup>
            )}
          </>
        )}
      </select>
      <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}
        className="px-3 py-2.5 rounded-xl border text-sm focus:border-[#E83E8C] outline-none"
        data-testid="staff-dept-select">
        {DEPARTMENTS.map((d) => (
          <option key={d.value} value={d.value}>{d.label}</option>
        ))}
      </select>
      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
        placeholder="WhatsApp phone (+91…)"
        className="px-3 py-2.5 rounded-xl border text-sm focus:border-[#E83E8C] outline-none"
        data-testid="staff-phone-input" />
      <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
        placeholder="Personal email (for nudges)" type="email"
        className="px-3 py-2.5 rounded-xl border text-sm focus:border-[#E83E8C] outline-none"
        data-testid="staff-email-input" />
      <input value={form.login_mobile} onChange={(e) => setForm({ ...form, login_mobile: e.target.value })}
        placeholder="Login mobile for OTP (+91…)"
        className="px-3 py-2.5 rounded-xl border text-sm focus:border-[#E83E8C] outline-none"
        data-testid="staff-login-mobile-input" />
      <div className="sm:col-span-2 lg:col-span-3">
        <label className="text-[11px] font-semibold text-[#1E1B4B] mb-1 block">
          Reporting manager
          {candidatesRole && (
            <span className="ml-1 text-[10px] text-indigo-600">
              · suggested role: <span className="font-mono">{candidatesRole}</span>
            </span>
          )}
        </label>
        <select value={form.reporting_manager}
          onChange={(e) => setForm({ ...form, reporting_manager: e.target.value })}
          className="w-full px-3 py-2 rounded-xl border text-sm focus:border-[#E83E8C] outline-none"
          data-testid="staff-reporting-manager-select">
          <option value="">— none (top-level) —</option>
          {managerCandidates.map((c) => (
            <option key={c.username} value={c.username}>
              {c.name} · {c.role}
            </option>
          ))}
        </select>
        <p className="text-[10px] text-gray-400 mt-0.5">
          {candidatesRole
            ? `Showing employees with role "${candidatesRole}". If you need someone else, the Reporting Hierarchy card (below) lets you change it anytime.`
            : "Showing every active employee. Set a designation's 'reports to' to auto-filter this list."}
        </p>
      </div>
    </div>
    <p className="text-[10px] text-gray-400 -mt-2">
      Phone &amp; email power HR nudges (policy re-sign reminders when deadline &lt; 48h). Both optional.
    </p>
    {headcountHint && (
      <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded" data-testid="headcount-warning">
        {headcountHint}
      </p>
    )}
    {addError && (
      <p className="text-red-500 text-sm" data-testid="add-staff-error">{addError}</p>
    )}
    <div className="flex gap-2">
      <button onClick={addManager} disabled={addLoading}
        className="px-5 py-2 bg-[#25D366] text-white rounded-xl text-sm font-medium hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
        data-testid="save-staff-btn">
        {addLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
      </button>
      <button onClick={onCancel}
        className="px-5 py-2 bg-gray-100 text-gray-500 rounded-xl text-sm">
        Cancel
      </button>
    </div>
  </div>
);
